import { getLanguage } from "@webbloqs/elements/lib/i18n";
import { type ClientContext } from "@quatico/magellan-shared";

export const CPQ_NO_AUTH_ENDPOINT = "/cds/cpq/api/no-auth";
export const CPQ_NO_AUTH_NAMESPACE = "cds-cpq-no-auth";

export const CPQ_AUTH_NAMESPACE = "cds-cpq-auth";
export const CPQ_AUTH_ENDPOINT = "/cds/cpq/api/auth";

export const TRANSPORT_NAME = "cds-fetch-transport";

// WIP DEV
export const APPOINTMENTS_NO_AUTH_NAMESPACE = "appointments-cds-no-auth";
export const APPOINTMENTS_TRANSPORT_NAME = "appointments-cds-fetch-transport";
export const APPOINTMENTS_NO_AUTH_ENDPOINT = "/cds/appointments/api/no-auth";

type KnownEndpoint = typeof CPQ_NO_AUTH_ENDPOINT | typeof APPOINTMENTS_NO_AUTH_ENDPOINT;

// WIP DEV - should be Record<KnownEndpoint, string>
const browserEndpointConfig: Record<string, Record<string, string>> = {
    "localhost:3000": {
        [CPQ_NO_AUTH_ENDPOINT]: "http://localhost:3000",
        [CPQ_AUTH_ENDPOINT]: "http://localhost:3000",
        [APPOINTMENTS_NO_AUTH_ENDPOINT]: "http://localhost:4000",
    },
    "localhost:3001": {
        [CPQ_NO_AUTH_ENDPOINT]: "http://localhost:3000",
        [CPQ_AUTH_ENDPOINT]: "http://localhost:3000",
        [APPOINTMENTS_NO_AUTH_ENDPOINT]: "http://localhost:4000",
    },
    "localhost:6006": {
        [CPQ_NO_AUTH_ENDPOINT]: "http://localhost:3000",
        [CPQ_AUTH_ENDPOINT]: "http://localhost:3000",
    },
    "localhost:6007": {
        [CPQ_NO_AUTH_ENDPOINT]: "http://localhost:3000",
        [CPQ_AUTH_ENDPOINT]: "http://localhost:3000",
    },
};

export const getTargetUrlForEndpointByHostname = (relativeEndpoint: string): string => {
    // browser config
    const hostname =
        globalThis?.location?.hostname === "localhost"
            ? `${globalThis?.location?.hostname}:${globalThis?.location?.port}`
            : globalThis?.location?.hostname;
    const endpointsForHostname = browserEndpointConfig[hostname];
    if (!endpointsForHostname) {
        return relativeEndpoint;
    }
    const origin = endpointsForHostname[relativeEndpoint as KnownEndpoint];
    if (!origin) {
        throw new Error(
            "Configuration error (browser config). " +
                `Missing "origin" property for endpoint: "${relativeEndpoint}".`
        );
    }
    return `${origin}${relativeEndpoint}`;
};

const createFormData = ({
    name,
    payload,
    namespace,
}: {
    name: string;
    payload: string;
    namespace: string;
}): FormData => {
    const formData = new FormData();
    formData.set("name", name);
    formData.set("data", payload);
    formData.set("namespace", namespace);
    formData.set("language", getLanguage() || "de");
    if (namespace === CPQ_AUTH_NAMESPACE) {
        const isBrowser = typeof window !== "undefined";
        if (isBrowser) {
            const hash = window.location.hash;
            const token = new URLSearchParams(hash.split("?")[1]).get("token");
            if (!token) {
                throw new Error("Token not found");
            }
            formData.set("token", token);
        } else {
            throw new Error("Token not supported in server environment");
        }
    }
    return formData;
};

export const cdsFetchFn = async (
    func: { name: string; payload: string; namespace: string; endpoint: string },
    context: ClientContext
) => {
    const { name, payload, namespace, endpoint } = func;
    if (!name) {
        throw new Error('Cannot invoke this remote function without "name" property.');
    }
    try {
        const isBrowser = typeof window !== "undefined";
        const url = getTargetUrlForEndpointByHostname(endpoint);
        const response = await fetch(url, {
            method: "POST",
            headers: { ...(context?.client?.headers ?? {}) },
            // include credentials for local development when sending requests from the browser
            credentials: isBrowser && window.location.hostname === "localhost" ? "include" : "same-origin",
            body: createFormData({ name, payload, namespace }),
        });
        if (response.status >= 300) {
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            return Promise.reject({ status: response.status, message: response?.statusText || "" });
        }
        return await response.text();
    } catch (err) {
        throw new Error(`Cannot invoke remote function: "${name}". Reason: "${err}".`);
    }
};
